import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Autocomplete,
  TextField,
  Chip,
} from '@mui/material';
import { usersAPI, groupsAPI } from '../services/api';

function AddEntitlementsDialog({ open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data from API
  const [allUsers, setAllUsers] = useState([]);
  const [allGroups, setAllGroups] = useState([]);

  // Selected values
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  const loadData = async () => {
    setInitialLoading(true);
    setError('');

    try {
      const [usersRes, groupsRes] = await Promise.all([
        usersAPI.list(),
        groupsAPI.list(),
      ]);

      setAllUsers(usersRes.data.users || []);
      setAllGroups(groupsRes.data.groups || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users and groups');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedUsers.length === 0) {
      setError('Please select at least one user');
      return;
    }

    if (selectedGroups.length === 0) {
      setError('Please select at least one group');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Prepare all API calls
      const apiCalls = [];

      // For each group, add all selected users
      for (const group of selectedGroups) {
        const usernames = selectedUsers.map(u => u.username);
        apiCalls.push(
          groupsAPI.addMembers(group.name, usernames)
        );
      }

      // Execute all API calls concurrently
      await Promise.all(apiCalls);

      setSuccess(
        `Successfully added ${selectedUsers.length} user(s) to ${selectedGroups.length} group(s)`
      );

      // Reset form
      setTimeout(() => {
        setSelectedUsers([]);
        setSelectedGroups([]);
        if (onSuccess) onSuccess();
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add entitlements');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedUsers([]);
      setSelectedGroups([]);
      setError('');
      setSuccess('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Entitlements (Group Memberships)</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Add users to groups
            </Typography>
            <Typography variant="body2">
              Select one or more users and one or more groups. The selected users will be added
              to all selected groups concurrently.
            </Typography>
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {initialLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Autocomplete
                multiple
                options={allUsers}
                getOptionLabel={(option) =>
                  `${option.username} (${option.given_name || ''} ${option.surname || ''})`
                }
                value={selectedUsers}
                onChange={(event, newValue) => {
                  setSelectedUsers(newValue);
                  setError('');
                }}
                disabled={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Users"
                    placeholder="Start typing to search users..."
                    helperText="Type username or name to search"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.username}
                      {...getTagProps({ index })}
                      key={option.username}
                    />
                  ))
                }
                sx={{ mb: 3 }}
              />

              <Autocomplete
                multiple
                options={allGroups}
                getOptionLabel={(option) => option.name}
                value={selectedGroups}
                onChange={(event, newValue) => {
                  setSelectedGroups(newValue);
                  setError('');
                }}
                disabled={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Groups"
                    placeholder="Select groups to add users to..."
                    helperText="Select one or more groups"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.name}
                      {...getTagProps({ index })}
                      key={option.name}
                      color="primary"
                    />
                  ))
                }
              />

              {selectedUsers.length > 0 && selectedGroups.length > 0 && (
                <Alert severity="info" sx={{ mt: 3 }}>
                  <Typography variant="body2">
                    <strong>Summary:</strong> Add {selectedUsers.length} user(s) to {selectedGroups.length} group(s)
                    <br />
                    <strong>Total operations:</strong> {selectedGroups.length} (one per group)
                  </Typography>
                </Alert>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || initialLoading || selectedUsers.length === 0 || selectedGroups.length === 0}
        >
          {loading ? <CircularProgress size={24} /> : 'Add Entitlements'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddEntitlementsDialog;
